import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import * as authApi from '@/api/auth';
import { tokenStore, bindRefreshHook } from '@/api/http';
import type { AuthIdentity, IssuedTokens, RoleCode } from '@/types';

export const useAuthStore = defineStore('auth', () => {
  const tokens = ref<IssuedTokens | null>(tokenStore.get());
  const identity = ref<AuthIdentity | null>(null);
  const initialized = ref(false);

  const isAuthenticated = computed(() => !!tokens.value?.accessToken && !!identity.value);
  const roles = computed<RoleCode[]>(() => identity.value?.roles ?? []);
  const permissions = computed<string[]>(() => identity.value?.permissions ?? []);
  const displayName = computed(() => identity.value?.displayName ?? '');
  const accountKind = computed(() => identity.value?.accountKind ?? 'BRAND');

  function setTokens(next: IssuedTokens | null): void {
    tokens.value = next;
    if (next) tokenStore.set(next);
    else tokenStore.clear();
  }

  function setIdentity(next: AuthIdentity | null): void {
    identity.value = next;
  }

  async function login(loginName: string, password: string): Promise<void> {
    const issued = await authApi.login({ loginName, password });
    setTokens(issued);
    await fetchProfile();
  }

  async function fetchProfile(): Promise<void> {
    if (!tokens.value?.accessToken) {
      identity.value = null;
      return;
    }
    try {
      const me = await authApi.fetchMe();
      identity.value = me;
    } catch {
      identity.value = null;
      setTokens(null);
    }
  }

  async function logout(): Promise<void> {
    if (tokens.value?.accessToken) {
      try {
        await authApi.logout(tokens.value.accessToken);
      } catch {
        // ignore network errors during logout
      }
    }
    setTokens(null);
    setIdentity(null);
  }

  async function bootstrap(): Promise<void> {
    if (initialized.value) return;
    initialized.value = true;
    bindRefreshHook({
      refresh: (rt) => authApi.refresh(rt),
      onUnauthorized: () => {
        setTokens(null);
        setIdentity(null);
      },
    });
    if (tokens.value?.accessToken) {
      await fetchProfile();
    }
  }

  return {
    tokens,
    identity,
    isAuthenticated,
    roles,
    permissions,
    displayName,
    accountKind,
    login,
    logout,
    fetchProfile,
    bootstrap,
    setTokens,
    setIdentity,
  };
});
